/*
 * 設定画面向け SSO Token Swap。
 * Cookie（または既存 Amplify セッション）の access_token を challenge answer にし、
 * 呼び出し元 client_id 向けに CUSTOM_AUTH でトークンを再発行して Amplify / Cookie を揃える。
 * Authorize Lambda の SSO Token Swap と同型。
 */

import { Amplify, Auth } from 'aws-amplify';
import {
	CognitoUser,
	CognitoUserPool,
	CognitoUserSession,
	CognitoIdToken,
	CognitoAccessToken,
	CognitoRefreshToken,
	AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import axios from 'axios';
import jwt_decode from 'jwt-decode';

import awsconfig from '../aws-exports';
import { getCookie } from '../components/LandingPage/helpers/cookieHelper';
import {
	setTokenCookie,
	setRefreshTokenCookie,
	TOKEN_CLIENT_ID_COOKIE,
} from '../components/LandingPage/helpers/tokenHelper';

var Config = require('Config');

const CLOCK_SKEW_MS = 30 * 1000;

function isJwtExpired(token) {
	try {
		const { exp } = jwt_decode(token);
		return !exp || exp * 1000 < Date.now() + CLOCK_SKEW_MS;
	} catch (e) {
		return true;
	}
}

function clientIdFromAccessToken(accessToken) {
	if (!accessToken) {
		return null;
	}
	try {
		const payload = jwt_decode(accessToken);
		return payload.client_id || null;
	} catch (e) {
		return null;
	}
}

/**
 * refresh に使う発行元 client_id。
 * access JWT → token_client_id Cookie → localStorage の順。
 */
export function resolveIssuerClientId(accessToken) {
	const fromAccess = clientIdFromAccessToken(accessToken);
	if (fromAccess) {
		return fromAccess;
	}
	return (
		getCookie(TOKEN_CLIENT_ID_COOKIE) ||
		localStorage.getItem('client-id') ||
		awsconfig.aws_user_pools_web_client_id
	);
}

function resolveUsername(idToken, accessToken) {
	try {
		const accessPayload = jwt_decode(accessToken);
		if (accessPayload.username) {
			return accessPayload.username;
		}
	} catch (e) {
		// ignore
	}
	try {
		const idPayload = jwt_decode(idToken);
		return idPayload['cognito:username'] || idPayload['username'] || idPayload['sub'];
	} catch (e) {
		return null;
	}
}

export function configureAmplifyForClient(clientId) {
	const amplifyConfig = {
		...awsconfig,
		aws_user_pools_web_client_id: clientId,
		Auth: {
			authenticationFlowType:
				Config.authenticationFlowType !== undefined
					? Config.authenticationFlowType
					: 'USER_SRP_AUTH',
		},
	};
	Amplify.configure(amplifyConfig);
	localStorage.setItem('client-id', clientId);
}

async function refreshTokens(refreshToken, clientId) {
	const response = await axios.post(
		'/oauth2/token',
		new URLSearchParams({
			grant_type: 'refresh_token',
			client_id: clientId,
			refresh_token: refreshToken,
		}).toString(),
		{
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		}
	);
	return response.data;
}

function applyAmplifySession({ clientId, username, idToken, accessToken, refreshToken }) {
	configureAmplifyForClient(clientId);

	const userPool = new CognitoUserPool({
		UserPoolId: awsconfig.aws_user_pools_id,
		ClientId: clientId,
	});
	const cognitoUser = new CognitoUser({
		Username: username,
		Pool: userPool,
	});
	const session = new CognitoUserSession({
		IdToken: new CognitoIdToken({ IdToken: idToken }),
		AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
		RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken || '' }),
	});
	cognitoUser.setSignInUserSession(session);
}

function initiateCustomAuth(cognitoUser, authenticationDetails) {
	return new Promise(function (resolve, reject) {
		cognitoUser.initiateAuth(authenticationDetails, {
			onSuccess: resolve,
			onFailure: reject,
			customChallenge: resolve,
		});
	});
}

function sendCustomChallengeAnswer(cognitoUser, challengeResponse) {
	return new Promise(function (resolve, reject) {
		cognitoUser.sendCustomChallengeAnswer(challengeResponse, {
			onSuccess: resolve,
			onFailure: reject,
			customChallenge: reject,
		});
	});
}

/**
 * Cookie の access_token で targetClientId 向けに Token Swap する。
 * @returns {Promise<boolean>}
 */
async function performTokenSwap(accessToken, targetClientId) {
	const username = jwt_decode(accessToken).username;
	if (!username) {
		console.warn('performTokenSwap: username missing from access_token');
		return false;
	}

	const userPool = new CognitoUserPool({
		UserPoolId: awsconfig.aws_user_pools_id,
		ClientId: targetClientId,
	});
	const cognitoUser = new CognitoUser({
		Username: username,
		Pool: userPool,
	});
	cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH');

	const authenticationDetails = new AuthenticationDetails({
		Username: username,
	});

	await initiateCustomAuth(cognitoUser, authenticationDetails);
	const result = await sendCustomChallengeAnswer(cognitoUser, accessToken);

	const idToken = result.getIdToken().getJwtToken();
	const newAccessToken = result.getAccessToken().getJwtToken();
	const refreshToken = result.getRefreshToken().getToken();

	if (!idToken || !newAccessToken || !refreshToken) {
		console.warn('performTokenSwap: tokens missing from CUSTOM_AUTH result');
		return false;
	}

	setTokenCookie('id_token', idToken);
	setTokenCookie('access_token', newAccessToken);
	setRefreshTokenCookie(refreshToken, newAccessToken);

	applyAmplifySession({
		clientId: targetClientId,
		username,
		idToken,
		accessToken: newAccessToken,
		refreshToken,
	});

	await Auth.currentAuthenticatedUser();
	return true;
}

/**
 * Cookie から有効な id/access（必要なら refresh）を用意する。
 * @returns {Promise<{idToken, accessToken, refreshToken, clientId}|null>}
 */
async function ensureValidTokensFromCookies() {
	let idToken = getCookie('id_token');
	let accessToken = getCookie('access_token');
	const refreshToken = getCookie('refresh_token');

	if (!refreshToken && (!idToken || !accessToken)) {
		return null;
	}

	const needsRefresh =
		!accessToken ||
		!idToken ||
		isJwtExpired(accessToken) ||
		isJwtExpired(idToken);

	if (needsRefresh) {
		if (!refreshToken) {
			return null;
		}
		const issuerClientId = resolveIssuerClientId(accessToken);
		if (!issuerClientId) {
			console.warn('ensureValidTokensFromCookies: issuer client_id could not be resolved');
			return null;
		}
		try {
			const tokens = await refreshTokens(refreshToken, issuerClientId);
			accessToken = tokens.access_token;
			idToken = tokens.id_token;
			if (!accessToken || !idToken) {
				return null;
			}
			setTokenCookie('id_token', idToken);
			setTokenCookie('access_token', accessToken);
			setRefreshTokenCookie(refreshToken, accessToken);
		} catch (e) {
			console.warn('ensureValidTokensFromCookies: token refresh failed', e);
			return null;
		}
	}

	const clientId = clientIdFromAccessToken(accessToken);
	if (!clientId) {
		return null;
	}

	return { idToken, accessToken, refreshToken: getCookie('refresh_token'), clientId };
}

/**
 * targetClientId 向けに Amplify セッションを確立する。
 * Cookie / 既存セッションが別 client の場合は CUSTOM_AUTH で Token Swap する。
 *
 * @param {string|null|undefined} targetClientId URL または localStorage の client_id。未指定時は Cookie 発行元のまま復元。
 * @returns {Promise<boolean>}
 */
export async function ensureSessionForClient(targetClientId) {
	const desiredClientId =
		(targetClientId && String(targetClientId).trim()) || null;

	// 1. 既に Amplify が desired（または未指定時は任意）でログイン済みか
	try {
		await Auth.currentAuthenticatedUser();
		const session = await Auth.currentSession();
		const accessToken = session.getAccessToken().getJwtToken();
		const sessionClientId = clientIdFromAccessToken(accessToken);

		if (!desiredClientId || sessionClientId === desiredClientId) {
			return true;
		}

		// 別 client でログイン済み → 有効な access があれば swap
		if (accessToken && !isJwtExpired(accessToken)) {
			try {
				return await performTokenSwap(accessToken, desiredClientId);
			} catch (e) {
				console.warn('ensureSessionForClient: swap from Amplify session failed', e);
			}
		}
	} catch (err) {
		if (err !== 'not authenticated') {
			console.log(err);
		}
	}

	// 2. Cookie から有効トークンを確保
	const cookieTokens = await ensureValidTokensFromCookies();
	if (!cookieTokens) {
		return false;
	}

	const { idToken, accessToken, refreshToken, clientId } = cookieTokens;
	const username = resolveUsername(idToken, accessToken);
	if (!username) {
		console.warn('ensureSessionForClient: username could not be resolved');
		return false;
	}

	const finalClientId = desiredClientId || clientId;

	// 3. 同 client → Amplify に載せるだけ
	if (clientId === finalClientId) {
		try {
			applyAmplifySession({
				clientId,
				username,
				idToken,
				accessToken,
				refreshToken,
			});
			await Auth.currentAuthenticatedUser();
			return true;
		} catch (e) {
			console.warn('ensureSessionForClient: failed to restore Amplify session', e);
			return false;
		}
	}

	// 4. クロス client → Token Swap
	try {
		return await performTokenSwap(accessToken, finalClientId);
	} catch (e) {
		console.warn('ensureSessionForClient: token swap failed', e);
		return false;
	}
}
